import React, { useEffect, useState } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import Button from '@mui/joy/Button';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Checkbox from '@mui/joy/Checkbox';
import Alert from '@mui/joy/Alert';
import CircularProgress from '@mui/joy/CircularProgress';
import { supplierProductsApi } from '../../api/supplierProducts.api';
import { categoriesApi } from '../../api/categories.api';
import { Category } from '../../types/category';
import { SupplierProduct, SupplierProductImportRow } from '../../types/supplierProduct';

interface SupplierProductImportModalProps {
  open: boolean;
  supplierClientReference: string;
  messageReferences: string[];
  onClose: () => void;
  onSuccess: () => void;
}

const SupplierProductImportModal: React.FC<SupplierProductImportModalProps> = ({
  open,
  supplierClientReference,
  messageReferences,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [rows, setRows] = useState<SupplierProductImportRow[]>([]);
  const [manualSearchTerm, setManualSearchTerm] = useState<Record<string, string>>({});
  const [manualSearchResults, setManualSearchResults] = useState<Record<string, SupplierProduct[]>>({});

  useEffect(() => {
    if (!open) {
      return;
    }
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [categoriesData, extractData] = await Promise.all([
          categoriesApi.getAll(),
          supplierProductsApi.extract(messageReferences, supplierClientReference),
        ]);
        setCategories(categoriesData);
        setRows(
          extractData.candidates.map((candidate, index) => {
            const matchedCategory = categoriesData.find(
              (category) =>
                category.name.toLowerCase() === candidate.categorySuggestion.toLowerCase(),
            );
            return {
              id: `${candidate.sourceMessageReference}-${String(index)}`,
              name: candidate.name,
              price: candidate.price,
              description: candidate.description,
              categoryReference: matchedCategory?.reference,
              categorySuggestion: candidate.categorySuggestion,
              sourceMessageReference: candidate.sourceMessageReference,
              suggestedMatchReference: candidate.suggestedMatchReference,
              suggestedMatchName: candidate.suggestedMatchName,
              useSuggestedMatch: Boolean(candidate.suggestedMatchReference),
            };
          }),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al extraer productos');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [open, messageReferences, supplierClientReference]);

  const updateRow = (id: string, patch: Partial<SupplierProductImportRow>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const searchManualMatch = async (rowId: string, term: string) => {
    setManualSearchTerm((prev) => ({ ...prev, [rowId]: term }));
    if (term.trim().length < 2) {
      setManualSearchResults((prev) => ({ ...prev, [rowId]: [] }));
      return;
    }
    const results = await supplierProductsApi.search(term);
    setManualSearchResults((prev) => ({ ...prev, [rowId]: results }));
  };

  const handleSave = async () => {
    if (rows.length === 0) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await supplierProductsApi.bulkCreate(
        supplierClientReference,
        rows.map((row) => ({
          name: row.name,
          price: row.price,
          description: row.description,
          categoryReference: row.categoryReference,
          sourceMessageReference: row.sourceMessageReference,
          linkToSupplierProductReference: row.useSuggestedMatch
            ? row.suggestedMatchReference ?? undefined
            : row.linkToSupplierProductReference,
        })),
      );
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar productos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 'min(960px, 96vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        <Typography level="h4">Importar productos de proveedor</Typography>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
            <Typography level="body-sm" sx={{ mt: 2 }}>
              Analizando mensajes...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {error ? <Alert color="danger">{error}</Alert> : null}
            {rows.length === 0 ? (
              <Typography level="body-sm">
                No se detectaron productos en los mensajes seleccionados.
              </Typography>
            ) : (
              rows.map((row) => (
                <Stack
                  key={row.id}
                  spacing={1.5}
                  sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'md' }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <FormControl sx={{ flex: 2 }}>
                      <FormLabel>Nombre</FormLabel>
                      <Input
                        value={row.name}
                        onChange={(e) => updateRow(row.id, { name: e.target.value })}
                      />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Precio</FormLabel>
                      <Input
                        type="number"
                        value={row.price}
                        onChange={(e) =>
                          updateRow(row.id, { price: Number(e.target.value) || 0 })
                        }
                      />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Categoría</FormLabel>
                      <Select
                        value={row.categoryReference ?? ''}
                        onChange={(_, value) =>
                          updateRow(row.id, { categoryReference: (value as string) || undefined })
                        }
                        placeholder={row.categorySuggestion || 'Seleccionar'}
                      >
                        {categories.map((category) => (
                          <Option key={category.reference} value={category.reference}>
                            {category.name}
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <FormControl>
                    <FormLabel>Descripción</FormLabel>
                    <Textarea
                      minRows={2}
                      value={row.description ?? ''}
                      onChange={(e) => updateRow(row.id, { description: e.target.value })}
                    />
                  </FormControl>
                  {row.suggestedMatchReference ? (
                    <Checkbox
                      label={`Vincular a producto existente: ${row.suggestedMatchName}`}
                      checked={row.useSuggestedMatch}
                      onChange={(e) =>
                        updateRow(row.id, { useSuggestedMatch: e.target.checked })
                      }
                    />
                  ) : null}
                  {!row.useSuggestedMatch ? (
                    <Stack spacing={1}>
                      <FormLabel>Vincular manualmente a producto existente</FormLabel>
                      <Input
                        placeholder="Buscar producto..."
                        value={manualSearchTerm[row.id] ?? ''}
                        onChange={(e) => searchManualMatch(row.id, e.target.value)}
                      />
                      {(manualSearchResults[row.id] ?? []).map((product) => (
                        <Button
                          key={product.reference}
                          size="sm"
                          variant={
                            row.linkToSupplierProductReference === product.reference
                              ? 'solid'
                              : 'outlined'
                          }
                          onClick={() =>
                            updateRow(row.id, {
                              linkToSupplierProductReference: product.reference,
                            })
                          }
                        >
                          {product.name}
                        </Button>
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              ))
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" color="neutral" onClick={onClose}>
                Cancelar
              </Button>
              <Button loading={saving} onClick={handleSave} disabled={rows.length === 0}>
                Guardar productos
              </Button>
            </Stack>
          </Stack>
        )}
      </ModalDialog>
    </Modal>
  );
};

export default SupplierProductImportModal;
